import { fetchAuthSession } from 'aws-amplify/auth';
import { markActivity } from '../utils/idleSession';

const API_BASE = 'https://0mnjso6bzi.execute-api.us-east-1.amazonaws.com/prod';
const UPLOAD_URL_ENDPOINT = `${API_BASE}/upload-url`;
const UPLOAD_STATUS_ENDPOINT = `${API_BASE}/upload-status`;

export const SUPPORT_EMAIL = 'support@256metrix.com';

// ─── Types ─────────────────────────────────────────────────────────────────────

export interface UploadUrlResponse {
  uploadUrl: string;
  s3Key: string;
  expiresIn: number;
  /** UTC time (ISO, ends in Z) taken by the server just before the upload URL was issued.
   *  A status row older than this belongs to an earlier attempt under the same file name. */
  serverTime?: string;
  /** The Content-Type the upload URL was signed for. The PUT must send exactly this value,
   *  or S3 refuses it. Absent on older servers: the page then derives it from the extension. */
  contentType?: string;
}

export interface FileUploadProgress {
  filename: string;
  progress: number;
  status: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
}

export type UploadRowStatus = 'processing' | 'ingested' | 'failed' | 'unknown' | 'not_found';

export interface UploadStatusRow {
  filename: string;
  status: UploadRowStatus;
  data_type?: string | null;
  updated_at?: string | null;
  /** Present only when status is 'failed'. Plain text, never HTML. */
  last_error?: string;
}

export interface RebuildStatusRow {
  year: string;
  month: string;
  kind: 'inbound' | 'outbound';
  status: 'queued' | 'processing' | 'complete' | 'failed' | 'unknown';
  current_step?: string | null;
  updated_at?: string | null;
  last_error?: string;
}

export interface UploadStatusList {
  serverTime: string;
  uploads: UploadStatusRow[];
  rebuilds: RebuildStatusRow[];
  truncated: boolean;
}

/** The server refused the request (for example: the file name breaks the naming rules).
 *  The message is already phrased for the user. */
export class UploadRejectedError extends Error {
  suggestedFolder?: string;
  constructor(message: string, suggestedFolder?: string) {
    super(message);
    this.name = 'UploadRejectedError';
    this.suggestedFolder = suggestedFolder;
  }
}

// ─── Upload folders and file-name rules ────────────────────────────────────────
// Mirrors the pipeline's "dashboard upload status contract" (section 3). The server
// makes the final decision; this table lets the page catch the most common mistake
// (right name, wrong folder) before anything is sent.

export interface FileTypeOption {
  value: string;                 // upload folder
  label: string;
  code: string;                  // type-code used in the file name
  kind: 'period' | 'lookup';
}

export const FILE_TYPES: FileTypeOption[] = [
  { value: 'tickets',                         label: 'Tickets',                                         code: 'tickets',                    kind: 'period' },
  { value: 'billing',                         label: 'Billing',                                         code: 'billing',                    kind: 'period' },
  { value: 'facility-outbound-tickets',       label: 'Facility Outbound Tickets',                       code: 'outbound',                   kind: 'period' },
  { value: 'facility-outbound-summary',       label: 'Facility Outbound Summary',                       code: 'outbound-summary',           kind: 'period' },
  { value: 'facility-inbound-transfers',      label: 'Facility Inbound Transfers',                      code: 'transfers',                  kind: 'period' },
  { value: 'facility-inbound-transfers-pct',  label: 'Facility Inbound Transfers (Percent Allocation)', code: 'transfers-pct',              kind: 'period' },
  { value: 'facility-material-summaries',     label: 'Facility Material Summaries',                     code: 'summary',                    kind: 'period' },
  { value: 'facility-customer-summary',       label: 'Facility Customer Summary',                       code: 'customer-summary',           kind: 'period' },
  { value: 'facility-recovery-profiles',      label: 'Facility Recovery Profiles',                      code: 'recovery-profiles',          kind: 'lookup' },
  { value: 'landfill-acceptance-rules',       label: 'Landfill Acceptance Rules',                       code: 'acceptance-rules',           kind: 'lookup' },
  { value: 'lookup-facilities',               label: 'Lookup: Facilities',                              code: 'facilities',                 kind: 'lookup' },
  { value: 'lookup-material-codes',           label: 'Lookup: Material Codes',                          code: 'material-codes',             kind: 'lookup' },
  { value: 'lookup-service-areas',            label: 'Lookup: Service Areas',                           code: 'service-areas',              kind: 'lookup' },
  { value: 'lookup-routes',                   label: 'Lookup: Routes',                                  code: 'lookup-routes',              kind: 'lookup' },
  { value: 'lookup-route-service-areas',      label: 'Lookup: Route Service Areas',                     code: 'lookup-route-service-areas', kind: 'lookup' },
  { value: 'lookup-facility-customers',       label: 'Lookup: Facility Customers',                      code: 'facility-customers',         kind: 'lookup' },
  { value: 'lookup-material-processing',      label: 'Lookup: Material Processing',                     code: 'material-processing',        kind: 'lookup' },
  { value: 'lookup-service-codes',            label: 'Lookup: Service Codes',                           code: 'service-codes',              kind: 'lookup' },
  { value: 'lookup-rdrs-collection-category', label: 'Lookup: RDRS Collection Category',                code: 'rdrs-category',              kind: 'lookup' },
  { value: 'landfill-capacity-limits',        label: 'Landfill Capacity Limits',                        code: 'capacity-limits',            kind: 'lookup' },
  { value: 'accounts',                        label: 'Accounts',                                        code: 'accounts',                   kind: 'lookup' },
  { value: 'services',                        label: 'Services',                                        code: 'services',                   kind: 'lookup' },
];

// ─── Accepted file types ───────────────────────────────────────────────────────
// .csv and .xlsx only. The old binary Excel format (.xls) is NOT accepted.
// The server signs each upload URL for the Content-Type below and returns it.

export const CONTENT_TYPES: Record<string, string> = {
  csv: 'text/csv',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};

const isSupportedExtension = (ext: string): boolean => Object.prototype.hasOwnProperty.call(CONTENT_TYPES, ext);

/** Value for the file picker's `accept` attribute. */
export const ACCEPT_ATTR = '.csv,.xlsx';

const EXT = '\\.(?:csv|xlsx)';

/** Largest .xlsx the processor accepts (it refuses bigger workbooks; checking here saves the upload). */
export const MAX_XLSX_BYTES = 25 * 1024 * 1024;
export const MAX_XLSX_MB = 25;
/** Row limit the processor applies to a workbook (its MAX_EXCEL_ROWS setting). The processor enforces it; this number is only for the help text. */
export const MAX_XLSX_ROWS = 200000;

/** The extension exactly as written ('csv', 'CSV', 'xlsx', ...), or '' when there is none. */
function rawExtension(filename: string): string {
  const dot = filename.lastIndexOf('.');
  return dot < 0 ? '' : filename.slice(dot + 1);
}

/** Lower-case extension of a file name ('csv', 'xlsx', ...), or '' when there is none. */
export function fileExtension(filename: string): string {
  return rawExtension(filename).toLowerCase();
}

/** True when the name ends in exactly '.csv' or '.xlsx'. Upper-case extensions (.CSV, .XLSX) are not
 *  allowed: the bucket's upload trigger is case-sensitive, so such a file would never be processed. */
export function hasLowercaseExtension(filename: string): boolean {
  const ext = rawExtension(filename);
  return isSupportedExtension(ext) && ext === ext.toLowerCase();
}

/** The Content-Type to send for this file name (text/csv when the extension is not recognised). */
export function contentTypeFor(filename: string): string {
  const ext = fileExtension(filename);
  return isSupportedExtension(ext) ? CONTENT_TYPES[ext] : CONTENT_TYPES.csv;
}

/** Plain-language reason a file cannot be uploaded because of its type, or null when the type is fine. */
export function fileTypeProblem(filename: string): string | null {
  const ext = fileExtension(filename);
  if (ext === 'xls') {
    return 'Old Excel (.xls) files are not accepted. Save the file as .xlsx or .csv and upload it again.';
  }
  if (!isSupportedExtension(ext)) {
    return 'Only CSV (.csv) and Excel (.xlsx) files can be uploaded.';
  }
  if (!hasLowercaseExtension(filename)) {
    return 'File extensions must be lowercase (.csv or .xlsx, not .CSV or .XLSX). Rename the file and upload it again.';
  }
  return null;
}

/** Plain-language reason a file is too big to upload (Excel only), or null when the size is fine. */
export function fileSizeProblem(file: File): string | null {
  if (fileExtension(file.name) === 'xlsx' && file.size > MAX_XLSX_BYTES) {
    return `This Excel file is larger than ${MAX_XLSX_MB} MB. Split it into smaller files or upload it as CSV.`;
  }
  return null;
}

const SOURCE = '[a-z0-9][a-z0-9-]{0,18}[a-z0-9]';                              // 2-20 characters
const PERIOD = '(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)20(?:2[0-9]|[3-9][0-9])';
const VERSION = '(?:-v(?:[2-9]|[1-9][0-9]+))?';                                // none, or -v2 and up

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

const NAME_PATTERNS: Record<string, RegExp> = {};
FILE_TYPES.forEach(t => {
  const code = escapeRegExp(t.code);
  NAME_PATTERNS[t.value] =
    t.kind === 'period'
      ? new RegExp(`^${SOURCE}-${code}-${PERIOD}${VERSION}${EXT}$`, 'i')
      : new RegExp(`^${SOURCE}-${code}${EXT}$`, 'i');
});

export function folderLabel(folder: string): string {
  return FILE_TYPES.find(t => t.value === folder)?.label ?? folder;
}

/** True when the file name follows the naming rules for this upload folder. */
export function nameMatchesFolder(filename: string, folder: string): boolean {
  const pattern = NAME_PATTERNS[folder];
  return !!pattern && hasLowercaseExtension(filename) && pattern.test(filename);
}

/** When the name is valid for a DIFFERENT folder than the one selected, return that folder. */
export function suggestFolderForName(filename: string, selectedFolder: string): string | undefined {
  if (!hasLowercaseExtension(filename)) return undefined;
  const others = FILE_TYPES.filter(t => t.value !== selectedFolder && NAME_PATTERNS[t.value].test(filename));
  if (others.length === 0) return undefined;
  // a name can fit two folders (outbound-summary / summary): prefer the longer type code
  others.sort((a, b) => b.code.length - a.code.length);
  return others[0].value;
}

export function exampleName(folder: string): string {
  const t = FILE_TYPES.find(f => f.value === folder);
  if (!t) return 'acs-tickets-aug2026.csv';
  return t.kind === 'period' ? `acs-${t.code}-aug2026.csv` : `acs-${t.code}.csv`;
}

// ─── API calls ─────────────────────────────────────────────────────────────────

async function authedPost(url: string, body: unknown): Promise<Response> {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  if (!token) {
    throw new Error('Not authenticated');
  }
  return fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
}

async function readJson(response: Response): Promise<any> {
  try {
    return await response.json();
  } catch {
    return {};
  }
}

export async function getUploadUrl(filename: string, fileType: string): Promise<UploadUrlResponse> {
  const response = await authedPost(UPLOAD_URL_ENDPOINT, { filename, fileType });

  if (!response.ok) {
    if (response.status === 401 || response.status === 403) {
      throw new Error('Your session has expired or you do not have access. Please sign in again.');
    }
    const body = await readJson(response);
    // 400 = the server refused this file (name, folder). Its message is written for the user.
    if (response.status === 400 && typeof body.error === 'string') {
      throw new UploadRejectedError(body.error, typeof body.suggestedFolder === 'string' ? body.suggestedFolder : undefined);
    }
    throw new Error('Could not start the upload. Please try again.');
  }

  return await response.json();
}

/** Recent uploads and report refreshes for the signed-in user's company. */
export async function getUploadStatusList(): Promise<UploadStatusList> {
  const response = await authedPost(UPLOAD_STATUS_ENDPOINT, { action: 'list' });
  if (!response.ok) {
    throw new Error(`Upload status unavailable (${response.status})`);
  }
  return await response.json();
}

/** Status of specific files uploaded in this session (at most 25 names). */
export async function getUploadStatuses(filenames: string[]): Promise<{ serverTime: string; uploads: UploadStatusRow[] }> {
  const response = await authedPost(UPLOAD_STATUS_ENDPOINT, { action: 'get', filenames: filenames.slice(0, 25) });
  if (!response.ok) {
    throw new Error(`Upload status unavailable (${response.status})`);
  }
  return await response.json();
}

export async function uploadFileToS3(
  file: File,
  uploadUrl: string,
  onProgress?: (progress: number) => void,
  contentType?: string
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (e) => {
      // A file that is still uploading is the user at work: it keeps the session alive.
      // (Status polling below deliberately does not.)
      markActivity();
      if (e.lengthComputable && onProgress) {
        const progress = Math.round((e.loaded / e.total) * 100);
        onProgress(progress);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status === 200) {
        resolve();
      } else {
        console.error('S3 upload failed:', xhr.status, xhr.statusText);
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed. Check your connection and try again.'));
    });

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload aborted'));
    });

    xhr.open('PUT', uploadUrl);
    // Must equal the type the URL was signed for: the server's value, else derived from the extension.
    // (Not file.type: browsers often leave it empty for .xlsx.)
    xhr.setRequestHeader('Content-Type', contentType || contentTypeFor(file.name));

    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target?.result) {
        xhr.send(e.target.result as ArrayBuffer);
      } else {
        reject(new Error('Failed to read file'));
      }
    };
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    reader.readAsArrayBuffer(file);
  });
}
